import { useAlertDialog } from '@/components/common/alert-dialog-provider';
import { ListItem } from '@/components/common/list-item';
import { ReportStatusBadge } from '@/components/common/report-status-badge';
import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { ReportsListSkeleton } from '@/components/skeletons/reports-list-skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isTransientStatus } from '@/lib/reports/status';
import { cn } from '@/lib/utils';
import { useConvexMutation } from '@convex-dev/react-query';
import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { usePaginatedQuery } from 'convex/react';
import { Calendar, Plus, SearchIcon, Trash2, XIcon } from 'lucide-react';
import { useQueryState } from 'nuqs';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';
import { isDefaultReportTitle } from '~/convex/lib/types';
import type { Report } from '~/convex/schema';

const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY_MS = 400; // Milliseconds

export const ReportsList = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useQueryState('q', {
    clearOnDefault: true,
    shallow: true,
  });
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_DELAY_MS);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Infinite query for listing reports.
  const {
    results: reports,
    status,
    isLoading,
    loadMore,
  } = usePaginatedQuery(
    api.reports.list,
    { searchQuery: debouncedSearchQuery ?? undefined },
    { initialNumItems: ITEMS_PER_PAGE },
  );

  // Observe if the element at the bottom of the screen becomes visible. If it
  // does, start loading the next page of reports.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // If the item is visible and we have more pages to load, fetch the next page.
        if (entries[0].isIntersecting && status !== 'Exhausted' && !isLoading) {
          loadMore(ITEMS_PER_PAGE);
        }
      },
      {
        root: null,
        rootMargin: '100px', // Pre-load 100px before reaching the bottom
        threshold: 0.1,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [status, isLoading, loadMore]);

  return (
    <>
      {/* <div className="flex min-h-0 w-full grow flex-col bg-white"> */}
      {/* Header */}
      <ResponsiveContainer
        scrollAwareBorder
        className="flex w-full flex-col items-start justify-center gap-4 bg-white px-4 py-4 md:items-start md:px-8 md:pt-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('report:title')}
        </h1>
        <Input
          placeholder={t('report:searchPlaceholder')}
          value={searchQuery ?? ''}
          className="w-full px-2"
          leading={
            <Button
              variant="ghost"
              size="icon"
              disabled
              className="disabled:opacity-100">
              <SearchIcon className="text-gray-500" />
            </Button>
          }
          trailing={
            searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery(null)}>
                <XIcon className="text-gray-500" />
              </Button>
            )
          }
          onChange={(e) => {
            const newValue = e.target.value !== '' ? e.target.value : null;
            setSearchQuery(newValue);
          }}
        />
      </ResponsiveContainer>

      {/* Content */}
      <ResponsiveContainer className="flex min-h-0 flex-1 grow flex-col space-y-3 overflow-y-auto">
        {/* Search */}

        {reports.length === 0 &&
        status === 'Exhausted' &&
        debouncedSearchQuery === null ? (
          <ReportsListEmpty />
        ) : reports.length === 0 &&
          status === 'Exhausted' &&
          debouncedSearchQuery !== null ? (
          <ReportsListSearchEmpty searchQuery={debouncedSearchQuery} />
        ) : (
          reports.length > 0 && (
            <div className="flex w-full flex-col gap-3 md:pb-4">
              {reports.map((report) => (
                <ReportItem key={report._id} report={report} />
              ))}
            </div>
          )
        )}

        {/* When this element becomes visible, start loading the next page of results. */}
        <div ref={loadMoreRef}>{isLoading && <ReportsListSkeleton />}</div>
      </ResponsiveContainer>
    </>
  );
};

const ReportItem = ({ report }: { report: Report }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const alertDialog = useAlertDialog();

  const { mutate: deleteReport, isPending: isDeleting } = useMutation({
    mutationFn: useConvexMutation(api.reports.remove),
  });

  const handleDelete = async (id: Id<'reports'>) => {
    const confirmed = await alertDialog.confirm({
      title: t('report:deleteConfirmTitle'),
      description: t('report:deleteConfirmDescription', {
        name: report.patientName,
      }),
      confirmText: t('report:delete'),
      cancelText: t('report:cancel'),
      variant: 'destructive',
    });

    if (confirmed) {
      deleteReport(
        { id },
        {
          onSuccess: () => {
            toast.success(t('report:deletedSuccessfully'));
          },
          onError: (error) => {
            toast.error(t('errors:errorDeletingReport'));
            console.error('Error deleting report:', error);
          },
        },
      );
    }
  };

  const createdAt = new Date(report._creationTime).toLocaleString(
    i18n.language,
    { dateStyle: 'medium', timeStyle: 'short' },
  );

  return (
    <ListItem
      title={
        <div className="flex flex-row items-center gap-2">
          <span
            className={cn(
              'truncate',
              isDefaultReportTitle(report.patientName) &&
                'text-muted-foreground italic',
            )}>
            {report.patientName}
          </span>
          <ReportStatusBadge status={report.status} />
        </div>
      }
      description={
        <div className="line-clamp-1 flex items-center truncate">
          <Calendar className="relative -top-0.25 mr-1 inline size-3.5 text-gray-500" />
          {createdAt}
          {t('report:separator')}
          {t('report:section', { count: report.generatedSections.length })}
        </div>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-red-100/80"
          loading={isDeleting}
          disabled={isDeleting || isTransientStatus(report.status)}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(report._id);
          }}>
          <Trash2 className="size-4 text-red-600" />
        </Button>
      }
      onClick={() => navigate(`/reports/${report._id}`)}
    />
  );
};

const ReportsListSearchEmpty = ({ searchQuery }: { searchQuery: string }) => {
  const { t } = useTranslation();

  return (
    <div className="relative w-full grow">
      <ReportsListSkeleton pulse={false} />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <p className="text-sm font-medium text-gray-900">
          {t('report:noReportsSearch', { searchQuery })}
        </p>
        <p className="mb-2 text-sm text-gray-500">
          {t('report:noReportsSearchDescription')}
        </p>
      </div>
    </div>
  );
};

const ReportsListEmpty = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="relative w-full grow">
      <ReportsListSkeleton pulse={false} />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm font-medium text-gray-900">
          {t('report:noReports')}
        </p>
        <p className="mb-2 text-sm text-gray-500">
          {t('report:noReportsDescription')}
        </p>
        <Button variant="outline" onClick={() => navigate('/')}>
          <Plus className="size-4" />
          {t('report:createReport')}
        </Button>
      </div>
    </div>
  );
};
